import React from 'react';
import { Linkedin, Twitter, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { teamMembers } from '../mockData';

const Team = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Our Team - Meet Arikone's Digital Marketing Experts"
        description="Meet the talented team behind Arikone's success. Our experienced digital marketing professionals, creative directors, and strategists are dedicated to growing your business."
        keywords="digital marketing team, marketing experts, creative team, digital strategists, marketing professionals noida"
      />

      {/* Hero Section */}
      <section className="relative bg-slate-50 py-24 overflow-hidden">
        {/* Background Mesh */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm mb-8 animate-fadeInUp">
            The Dream Team
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 animate-fadeInUp stagger-1 tracking-tight">
            The Faces Behind <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
              The Magic
            </span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12 animate-fadeInUp stagger-2 leading-relaxed">
            We are a diverse group of strategists, creators, and technologists united by a single mission: to help your brand thrive in the digital world.
          </p>

          {/* Floating Avatars Grid/Cloud */}
          <div className="relative h-20 sm:h-24 max-w-4xl mx-auto animate-fadeInUp stagger-3">
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center space-x-[-15px] sm:space-x-[-20px]">
              {[...Array(7)].map((_, i) => (
                <div key={i}
                  className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-slate-50 overflow-hidden shadow-lg transform hover:-translate-y-2 transition-transform duration-300`}
                  style={{ animation: `float 6s ease-in-out infinite ${i * 0.5}s` }}
                >
                  <img src={`https://i.pravatar.cc/100?img=${i + 15}`} alt="Team Member" className="w-full h-full object-cover" />
                </div>
              ))}
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-slate-50 bg-blue-600 flex items-center justify-center text-white font-bold text-sm sm:text-lg shadow-lg z-10">
                +20
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <Card
                key={member.id}
                className={`overflow-hidden hover-glow group cursor-pointer animate-fadeInUp stagger-${index + 1}`}
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={`${member.name} - ${member.role} at Arikone`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Social overlay on hover */}
                  <div className="absolute inset-0 bg-blue-600 bg-opacity-90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="flex space-x-4">
                      <a
                        href={member.social.linkedin}
                        className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors duration-300"
                        aria-label="LinkedIn"
                      >
                        <Linkedin size={24} />
                      </a>
                      <a
                        href={member.social.twitter}
                        className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors duration-300"
                        aria-label="Twitter"
                      >
                        <Twitter size={24} />
                      </a>
                      <a
                        href={`mailto:${member.social.email}`}
                        className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors duration-300"
                        aria-label="Email"
                      >
                        <Mail size={24} />
                      </a>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Join Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Join Our Team</h2>
          <p className="text-xl text-gray-600 mb-8">
            We're always looking for talented individuals who are passionate about digital marketing and
            creative excellence. If you want to be part of a dynamic, growing team, we'd love to hear from you.
          </p>
          <Card className="hover-glow">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Work With Us?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                {[
                  'Collaborative work environment',
                  'Exciting client projects',
                  'Professional growth opportunities',
                  'Competitive compensation',
                  'Work-life balance',
                  'Creative freedom'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <p className="text-gray-600 mb-4">
                  Send your resume to: <a href="mailto:careers@arikone.com" className="text-blue-600 font-semibold hover:underline">careers@arikone.com</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Our Culture</h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-12">
            At Arikone, we believe in fostering a culture of innovation, collaboration, and continuous learning
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Innovation First',
                description: 'We encourage creative thinking and experimenting with new ideas'
              },
              {
                title: 'Team Collaboration',
                description: 'Working together to achieve exceptional results for our clients'
              },
              {
                title: 'Continuous Growth',
                description: 'Regular training and development to stay ahead in the industry'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-blue-100">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;